import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import {
  PoButtonModule,
  PoFieldModule,
  PoNotificationService,
  PoSelectOption,
  PoWidgetModule
} from '@po-ui/ng-components';

import { firstValueFrom } from 'rxjs';

import { PdfParserService, PdfSemTextoError } from '../../notas-adicionar/pdf-parser/pdf-parser.service';
import { NfseData } from '../../notas-adicionar/pdf-parser/nfse-parser';
import { tomadorEntreFiliais } from '../../notas-adicionar/pdf-parser/tomador-filial';
import { casarCodigoServico } from '../../notas-adicionar/pdf-parser/codigo-servico';
import {
  CenarioSimulacao,
  Decisao,
  Opcao,
  RegraAplicada,
  RegrasApiService,
  ResolucaoPdf,
  Simulacao,
  SimulacaoNota
} from '../shared/regras-api.service';
import { PoComboListaVisivelDirective } from '../../../shared/po-combo-lista-visivel.directive';

/** Aba do cadastro que o resultado aponta, para o acesso direto da RF12. */
export interface AtalhoRegra {
  aba: 'cte' | 'nfse' | 'natureza' | 'produto';
  codigo: string;
}

/** Uma decisão já preparada para a tela. */
interface Cartao {
  titulo: string;
  valor: string;
  decisao: Decisao;
  aba?: AtalhoRegra['aba'];
  /** Mensagem da RF11 quando nenhuma regra cobre o cenário. */
  semCobertura?: string;
}

/**
 * Simulador de regra (MIT044 RF09 a RF12).
 *
 * Dois modos, três entradas: nota hipotética preenchida à mão, a mesma nota
 * hipotética preenchida a partir de um PDF, e nota já lançada.
 *
 * O PDF não é um modo separado de propósito. Ele reaproveita o PdfParserService
 * da inclusão manual e apenas PREENCHE o formulário, deixando tudo editável —
 * assim o usuário valida de uma vez as duas coisas que quer validar: o que foi
 * lido do documento e o que aquilo produz de classificação.
 *
 * A tela nunca decide nada: quem resolve é o motor no backend, o mesmo que
 * roda na entrega da nota. Um simulador com caminho próprio mente cedo ou tarde.
 */
@Component({
  selector: 'app-regras-simulador',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PoButtonModule,
    PoFieldModule,
    PoWidgetModule,
    PoComboListaVisivelDirective
  ],
  templateUrl: './simulador.html',
  styleUrls: ['./simulador.css']
})
export class RegrasSimuladorComponent {
  /** Pedido de abrir a regra no cadastro (RF12). */
  @Output() abrirRegra = new EventEmitter<AtalhoRegra>();

  private readonly fb = inject(FormBuilder);
  private readonly api = inject(RegrasApiService);
  private readonly notif = inject(PoNotificationService);
  private readonly pdf = inject(PdfParserService);

  modo: 'hipotetica' | 'nota' = 'hipotetica';
  loading = false;
  lendoPdf = false;

  resultado?: Simulacao;
  resultadoNota?: SimulacaoNota;
  nomePdf = '';

  /** O que o PDF nao respondeu e o operador precisa conferir antes de simular. */
  avisosPdf: string[] = [];

  readonly formCenario: FormGroup = this.fb.group({
    tipoDoc: ['nfse', Validators.required],
    filial: ['', Validators.required],
    tipoProcesso: ['terceiros'],
    geraFinanceiro: ['2'],
    simplesNacional: ['2'],
    icmsIsento: [''],
    issRetido: ['2'],
    cnpjTomador: [''],
    cnae: [''],
    valorTotal: [0],
    ir: [0],
    pis: [0],
    cofins: [0],
    csll: [0],
    inss: [0]
  });

  readonly formNota: FormGroup = this.fb.group({
    filial: ['', Validators.required],
    doc: ['', Validators.required],
    serie: [''],
    fornecedor: [''],
    loja: ['']
  });

  readonly modoOptions: PoSelectOption[] = [
    { label: 'Nota hipotética', value: 'hipotetica' },
    { label: 'Nota já lançada', value: 'nota' }
  ];

  readonly tipoDocOptions: PoSelectOption[] = [
    { label: 'NFS-e', value: 'nfse' },
    { label: 'CT-e', value: 'cte' }
  ];

  readonly tipoProcOptions: PoSelectOption[] = [
    { label: 'Conta e ordem / IPCOT', value: 'terceiros' },
    { label: 'Encomenda / Própria', value: 'encomenda' },
    { label: 'Sem processo', value: 'semprocesso' }
  ];

  readonly simNaoOptions: PoSelectOption[] = [
    { label: 'Sim', value: '1' },
    { label: 'Não', value: '2' }
  ];

  /**
   * O ICMS tem três estados, não dois: "não informado" faz o motor ignorar o
   * atributo, que é como o código antigo se comporta — ele nunca recebeu esse
   * dado. Colapsar em Sim/Não mudaria silenciosamente a classificação.
   */
  readonly icmsOptions: PoSelectOption[] = [
    { label: 'Não informado', value: '' },
    { label: 'Isento', value: '1' },
    { label: 'Tributado', value: '2' }
  ];

  get ehCte(): boolean {
    return this.formCenario.get('tipoDoc')?.value === 'cte';
  }

  /** Cartões do resultado, na ordem em que a MIT apresenta. */
  get cartoes(): Cartao[] {
    const sim = this.modo === 'nota' ? this.resultadoNota?.atual : this.resultado;
    if (!sim) { return []; }

    const lista: Cartao[] = [
      {
        titulo: 'Tipo de operação (TES)',
        valor: this.textoTes(sim.tes),
        decisao: sim.tes,
        aba: this.ehCteResultado ? 'cte' : 'nfse',
        semCobertura: sim.tes.regra ? undefined : this.faltaTes()
      },
      {
        titulo: 'Natureza financeira',
        valor: String(sim.natureza.valor ?? '') || '—',
        decisao: sim.natureza,
        aba: 'natureza',
        semCobertura: sim.natureza.regra ? undefined : 'Nenhuma natureza cadastrada cobre este perfil de retenção.'
      },
      {
        titulo: 'Produto',
        valor: String(sim.produto.valor ?? '').trim() || '—',
        decisao: sim.produto,
        aba: 'produto',
        semCobertura: sim.produto.regra ? undefined : 'Código de origem sem de-para cadastrado.'
      }
    ];

    if (sim.tomador) {
      lista.push({
        titulo: 'Tomador sem processo',
        valor: sim.tomador.valor === true ? 'Habilitado' : 'Não habilitado',
        decisao: sim.tomador
      });
    }

    return lista;
  }

  private get ehCteResultado(): boolean {
    if (this.modo === 'nota') {
      return this.resultadoNota?.cenario?.tipoDoc === 'cte';
    }
    return this.ehCte;
  }

  simular(): void {
    if (this.formCenario.invalid) {
      this.notif.warning('Informe ao menos a filial.');
      return;
    }

    this.loading = true;
    this.resultadoNota = undefined;

    this.api.simular(this.cenario()).subscribe({
      next: r => { this.resultado = r; this.loading = false; },
      error: e => {
        this.notif.error(e?.error?.message ?? 'Não foi possível simular.');
        this.loading = false;
      }
    });
  }

  simularNota(): void {
    if (this.formNota.invalid) {
      this.notif.warning('Informe a filial e o número da nota.');
      return;
    }

    this.loading = true;
    this.resultado = undefined;
    const v = this.formNota.getRawValue();

    this.api.simularNota(v).subscribe({
      next: r => {
        this.resultadoNota = r;
        this.loading = false;
        if (!r.temCenario && r.aviso) { this.notif.warning(r.aviso); }
      },
      error: e => {
        this.notif.error(e?.error?.message ?? 'Não foi possível localizar a nota.');
        this.loading = false;
      }
    });
  }

  /**
   * Lê o PDF no browser e preenche o formulário. Tudo que o documento informa
   * sai dele; os campos ficam editáveis, porque o operador confere de uma vez
   * o que foi lido e o que aquilo produz de classificação.
   *
   * A leitura reproduz a inclusão manual (notas-adicionar) passo a passo: o
   * mesmo parseDanfe, a mesma escolha de filial (tomadorEntreFiliais), o
   * mesmo casamento do código de serviço (casarCodigoServico) e os mesmos
   * padrões do CT-e (gera financeiro e tipo de processo "encomenda" quando o
   * processo não resolve). Simular com uma leitura diferente da inclusão
   * mostraria uma classificação que a nota nunca teria.
   *
   * O tipo de processo vem da referência externa ("AC 2882/26") consultada
   * na base (resolverPdf), como o resolverProcesso faz na inclusão.
   */
  async lerPdf(evento: Event): Promise<void> {
    const input = evento.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) { return; }

    this.lendoPdf = true;
    this.nomePdf = file.name;
    this.avisosPdf = [];

    try {
      const d = await this.pdf.parseDanfe(file);
      const ehCte = d.source === 'cte';

      this.formCenario.patchValue({
        tipoDoc: ehCte ? 'cte' : 'nfse',
        // Inclusão manual: CT-e nasce com financeiro; NFS-e sem.
        geraFinanceiro: ehCte ? '1' : '2',
        simplesNacional: d.simplesNacional ? '1' : '2',
        icmsIsento: d.icmsIsento === undefined ? '' : (d.icmsIsento ? '1' : '2'),
        cnpjTomador: d.cnpjTomador ?? '',
        cnae: d.cnaeServico ?? '',
        valorTotal: d.valorTotal ?? 0,
        issRetido: (d.retencoes?.iss ?? 0) > 0 ? '1' : '2',
        ir: d.retencoes?.ir ?? 0,
        pis: d.retencoes?.pis ?? 0,
        cofins: d.retencoes?.cofins ?? 0,
        csll: d.retencoes?.csll ?? 0,
        inss: d.retencoes?.inss ?? 0
      });

      // Mesmo default do applyDanfeToDoc: CT-e sem processo resolvido entra
      // como encomenda. O processo, se resolver, sobrescreve logo abaixo.
      if (ehCte) {
        this.formCenario.patchValue({ tipoProcesso: 'encomenda' });
      }

      if (!d.valorTotal) {
        this.avisosPdf.push('O valor total não foi localizado no documento: informe antes de simular.');
      }

      await Promise.all([
        this.completarFilial(d),
        this.completarProcesso(d),
        ehCte ? Promise.resolve() : this.completarCodigoServico(d)
      ]);

      this.notif.success(`PDF lido (${d.source}). Confira os campos e simule.`);
    } catch (e) {
      if (e instanceof PdfSemTextoError) {
        this.nomePdf = '';
        this.notif.warning('Este PDF não tem texto — é uma imagem digitalizada. Preencha os campos à mão.');
      } else {
        this.notif.error('Não foi possível ler este PDF.');
      }
    } finally {
      this.lendoPdf = false;
      input.value = '';
    }
  }

  /**
   * Filial pelo tomador, com a regra da inclusão manual: tomadorEntreFiliais
   * sobre a lista de filiais com CNPJ. Falha da consulta não interrompe a
   * leitura: o campo fica como estava e o operador escolhe.
   */
  private async completarFilial(d: NfseData): Promise<void> {
    let filiais: Opcao[] = [];
    try {
      filiais = await firstValueFrom(this.api.filiais());
    } catch {
      this.avisosPdf.push('Não foi possível consultar as filiais: escolha a filial da nota.');
      return;
    }

    const soDigitos = (c?: string) => (c ?? '').replace(/\D/g, '');
    const cnpjs = filiais.map(f => soDigitos(f.cnpj)).filter(c => c);

    // Backend anterior ao cnpj no lookup de filial: resolve so o tomador
    // rotulado pelo /regras/pdf/resolver, como a tela fazia antes.
    if (!cnpjs.length) {
      await this.completarFilialPeloServidor(soDigitos(d.cnpjTomador));
      return;
    }

    const tomador = tomadorEntreFiliais(d, cnpjs);

    if (tomador) {
      const filial = filiais.find(f => soDigitos(f.cnpj) === tomador);
      this.formCenario.patchValue({ filial: filial?.value ?? '', cnpjTomador: tomador });
      return;
    }

    const lidos = d.cnpjsDocumento ?? [];
    if (!lidos.length && !d.cnpjTomador) {
      this.avisosPdf.push('O documento não traz o CNPJ do tomador: escolha a filial da nota.');
    } else if (lidos.filter(c => cnpjs.includes(c)).length > 1) {
      this.avisosPdf.push('O documento traz mais de uma filial do grupo: escolha a filial tomadora.');
    } else {
      this.avisosPdf.push('Nenhum CNPJ impresso na nota é de filial do grupo: escolha a filial.');
    }
  }

  private async completarFilialPeloServidor(cnpj: string): Promise<void> {
    if (!cnpj) {
      this.avisosPdf.push('O documento não traz o CNPJ do tomador: escolha a filial da nota.');
      return;
    }
    try {
      const r = await firstValueFrom(this.api.resolverPdf(cnpj, ''));
      if (r.filial) {
        this.formCenario.patchValue({ filial: r.filial });
      } else {
        this.avisosPdf.push('O CNPJ do tomador impresso na nota não é de nenhuma filial: escolha a filial.');
      }
    } catch {
      this.avisosPdf.push('Não foi possível consultar a filial no cadastro: escolha a filial da nota.');
    }
  }

  /** Tipo de processo pela referência externa impressa no documento. */
  private completarProcesso(d: NfseData): Promise<void> {
    const processo = d.processo ?? '';
    if (!processo) {
      this.avisosPdf.push('O documento não traz a referência do processo: confira o tipo de processo.');
      return Promise.resolve();
    }

    return new Promise<void>(resolve => {
      this.api.resolverPdf('', processo).subscribe({
        next: r => {
          this.aplicarProcesso(r, processo);
          resolve();
        },
        error: () => {
          this.avisosPdf.push('Não foi possível consultar o processo no cadastro: confira o tipo de processo.');
          resolve();
        }
      });
    });
  }

  /** Aplica no formulário o tipo do processo, e avisa o que faltou. */
  private aplicarProcesso(r: ResolucaoPdf, processo: string): void {
    if (r.tipoProcesso) {
      // O cadastro distingue própria de encomenda; o simulador trata as duas
      // no mesmo cenário ("Encomenda / Própria"), então própria entra ali.
      const tipo = r.tipoProcesso === 'propria' ? 'encomenda' : r.tipoProcesso;
      this.formCenario.patchValue({ tipoProcesso: tipo });
    } else {
      this.avisosPdf.push(`O processo ${processo} não foi localizado: confira o tipo de processo.`);
    }

    if (r.processoEncerrado) {
      this.avisosPdf.push(`O processo ${processo} está encerrado.`);
    }
  }

  /**
   * Código de serviço no formato do cadastro, com a regra da inclusão manual
   * (casarCodigoServico): o PDF traz "33.01.01" e o de-para guarda "3301.01".
   * Sem correspondência o código lido fica no campo e o motor mostra que não
   * há de-para - que é exatamente o que aconteceria na entrega.
   */
  private async completarCodigoServico(d: NfseData): Promise<void> {
    const lido = d.cnaeServico ?? '';
    if (!lido) { return; }

    try {
      const codigos = await firstValueFrom(this.api.codigosServico());
      const casado = casarCodigoServico(lido, codigos);
      if (casado) {
        this.formCenario.patchValue({ cnae: casado });
      } else {
        this.avisosPdf.push(`O código de serviço ${lido} não tem de-para de produto cadastrado.`);
      }
    } catch {
      // Sem a lista, fica o código como foi lido.
    }
  }

  limparPdf(): void {
    this.nomePdf = '';
    this.avisosPdf = [];
  }

  /** RF12 — abre a regra na aba do cadastro. */
  irParaRegra(cartao: Cartao): void {
    if (!cartao.aba || !cartao.decisao.regra) { return; }
    this.abrirRegra.emit({ aba: cartao.aba, codigo: cartao.decisao.regra });
  }

  /** Pares comparados no modo nota lancada, tipados para o template. */
  readonly comparacoes: Array<{ chave: 'tes' | 'natureza' | 'produto'; rotulo: string }> = [
    { chave: 'tes', rotulo: 'TES' },
    { chave: 'natureza', rotulo: 'Natureza' },
    { chave: 'produto', rotulo: 'Produto' }
  ];

  regraAtualDe(qual: 'tes' | 'natureza' | 'produto'): string {
    return this.resultadoNota?.atual?.[qual]?.regra ?? '';
  }

  aplicadaDe(qual: 'tes' | 'natureza' | 'produto'): RegraAplicada | undefined {
    return this.resultadoNota?.aplicada?.[qual];
  }

  /** Divergência entre a regra gravada na entrega e a que venceria hoje. */
  divergiu(qual: 'tes' | 'natureza' | 'produto'): boolean {
    const gravada = this.aplicadaDe(qual)?.cod ?? '';
    const atual = this.resultadoNota?.atual?.[qual]?.regra ?? '';
    return !!gravada && !!atual && gravada !== atual;
  }

  vigenciaDe(ini?: string, fim?: string): string {
    const f = (d?: string) => (d && d.length === 8 ? `${d.slice(6, 8)}/${d.slice(4, 6)}/${d.slice(0, 4)}` : '');
    const a = f(ini);
    const b = f(fim);
    if (a && b) { return `${a} a ${b}`; }
    if (a) { return `desde ${a}`; }
    if (b) { return `até ${b}`; }
    return 'sem vigência definida';
  }

  private textoTes(d: Decisao): string {
    if (Array.isArray(d.valor)) {
      const ops = d.valor as Array<{ tes: string; desc: string }>;
      if (!ops.length) { return '—'; }
      return ops.map(o => o.tes).join(', ');
    }
    const n = Number(d.valor ?? 0);
    return n > 0 ? String(n) : '—';
  }

  private faltaTes(): string {
    const c = this.cenario();
    if (c.tipoDoc === 'cte') {
      return `Nenhuma regra de CT-e para filial ${c.filial}, processo ${c.tipoProcesso} e ` +
        `Simples Nacional ${c.simplesNacional ? 'sim' : 'não'}.`;
    }
    return `Nenhuma regra de NFS-e para processo ${c.tipoProcesso}, financeiro ` +
      `${c.geraFinanceiro ? 'sim' : 'não'} e ISS retido ${c.issRetido ? 'sim' : 'não'}.`;
  }

  private cenario(): CenarioSimulacao {
    const v = this.formCenario.getRawValue();
    const num = (x: unknown) => Number(x ?? 0) || 0;

    return {
      tipoDoc: v.tipoDoc,
      filial: (v.filial ?? '').trim(),
      tipoProcesso: v.tipoProcesso ?? '',
      geraFinanceiro: v.geraFinanceiro === '1',
      simplesNacional: v.simplesNacional === '1',
      // Vazio vira null: o backend distingue "não informado" de "tributado".
      icmsIsento: v.icmsIsento === '' ? null : v.icmsIsento === '1',
      issRetido: v.issRetido === '1',
      cnpjTomador: (v.cnpjTomador ?? '').replace(/\D/g, ''),
      cnae: (v.cnae ?? '').trim(),
      valorTotal: num(v.valorTotal),
      retencoes: {
        ir: num(v.ir), pis: num(v.pis), cofins: num(v.cofins),
        csll: num(v.csll), inss: num(v.inss)
      }
    };
  }
}
